import { Card, Col, Divider, Row, Typography } from "antd";

const { Text } = Typography;

const Preview = ({
  formData,
  templateProdDetail,
  parentOptions,
  tagOptions = [],
  statusData = [],
}) => {
  const overview = formData?.overview || {};
  const currencyConfiguration = formData?.currencyConfiguration || {};
  const postingControls = formData?.postingControls || {};
  const balanceManagement = formData?.balanceManagement || {};
  const reconcilationAndControl = formData?.reconcilationAndControl || {};

  const yesNo = (value) =>
    value === true ? "Yes" : value === false ? "No" : "-";

  const formatValue = (value) => {
    if (value === undefined || value === null || value === "") return "-";
    if (typeof value === "number") return value.toLocaleString();
    if (value?.format) return value.format("YYYY-MM-DD");
    return value;
  };

  const resolveOptionLabel = (options = [], value) => {
    if (value === undefined || value === null || value === "") return "-";
    const match = options.find(
      (option) => option.value === value || option.id === value,
    );
    if (!match) return String(value);
    return match.label || match.value || match.name || String(value);
  };

  // "All Currencies" sentinel + real currencies
  const currencyOptions = [
    { value: "ALL_CURRENCIES", label: "All Currencies" },
    ...(templateProdDetail?.currencies || []).map((c) => ({
      value: c.code,
      label: c.displayLabel || c.name || c.code,
    })),
  ];

  // ifrsClassificationOptions is [{category, options:[{id,code,value}]}]
  const allIfrsOptions = (
    templateProdDetail?.ifrsClassificationOptions || []
  ).flatMap((group) =>
    (group.options || []).map((o) => ({ value: o.id, label: o.value })),
  );

  const sections = [
    {
      title: "Overview",
      items: [
        {
          name: "Account Type",
          value: resolveOptionLabel(
            templateProdDetail?.accountTypeOptions,
            overview.type,
          ),
        },
        { name: "GL Code", value: formatValue(overview.glCode) },
        { name: "Account Name", value: formatValue(overview.name) },
        {
          name: "Short Name",
          value: formatValue(overview.shortName ?? overview.name),
        },
        {
          name: "Account Usage",
          value: resolveOptionLabel(
            templateProdDetail?.usageOptions,
            overview.usage,
          ),
        },
        {
          name: "Tag",
          value: resolveOptionLabel(tagOptions, overview.tagId),
        },
        {
          name: "Category",
          value: resolveOptionLabel(
            templateProdDetail?.categoryOptions,
            overview.category,
          ),
        },
        // {
        //   name: "Account Level",
        //   value: resolveOptionLabel(
        //     templateProdDetail?.accountLevelOptions,
        //     overview.accountLevel,
        //   ),
        // },
        {
          name: "Parent",
          value: resolveOptionLabel(parentOptions, overview.parentId),
        },
        {
          name: "Account Status",
          value: resolveOptionLabel(statusData, overview.accountStatus),
        },
        {
          name: "Natural Balance",
          value: resolveOptionLabel(
            templateProdDetail?.naturalBalanceOptions,
            overview.naturalBalance,
          ),
        },
        {
          name: "Reporting Code",
          value: formatValue(overview.reportingCode),
        },
        {
          name: "IFRS Classification",
          value: resolveOptionLabel(
            allIfrsOptions,
            overview.ifrsClassification,
          ),
        },
        {
          name: "Tax Category",
          value: resolveOptionLabel(
            templateProdDetail?.taxCategoryOptions,
            overview.taxCategory,
          ),
        },
        { name: "Opening Date", value: formatValue(overview.openingDate) },
        { name: "Closing Date", value: formatValue(overview.closingDate) },
      ],
    },
    {
      title: "Currency Configuration",
      items: [
        {
          name: "Currency Code",
          value: resolveOptionLabel(
            currencyOptions,
            currencyConfiguration.currencyCode,
          ),
        },
        {
          name: "Multi Currency Allowed",
          value: yesNo(currencyConfiguration.multiCurrencyAllowed),
        },
        {
          name: "Revaluation Required",
          value: yesNo(currencyConfiguration.revaluationRequired),
        },
        ...(currencyConfiguration.revaluationRequired
          ? [
              {
                name: "Revaluation GL Account",
                value: resolveOptionLabel(
                  templateProdDetail?.revaluationGlAccountOptions,
                  currencyConfiguration.revaluationGlAccountId,
                ),
              },
            ]
          : []),
      ],
    },
    {
      title: "Posting Controls",
      items: [
        {
          name: "Posting Allowed",
          value: yesNo(postingControls.postingAllowed),
        },
        { name: "Debit Allowed", value: yesNo(postingControls.debitAllowed) },
        {
          name: "Credit Allowed",
          value: yesNo(postingControls.creditAllowed),
        },
        {
          name: "Maximum Transaction Limit",
          value: formatValue(postingControls.maxTransactionLimit),
        },
        {
          name: "Batch Posting Allowed",
          value: yesNo(postingControls.batchPostingAllowed),
        },
      ],
    },
    {
      title: "Balance Management",
      items: [
        {
          name: "Opening Balance",
          value: formatValue(balanceManagement.openingBalance),
        },
        {
          name: "Minimum Balance Allowed",
          value: formatValue(balanceManagement.minimumBalanceAllowed),
        },
        {
          name: "Maximum Balance Allowed",
          value: formatValue(balanceManagement.maximumBalanceAllowed),
        },
        {
          name: "Overdraft Allowed",
          value: yesNo(balanceManagement.overdraftAllowed),
        },
      ],
    },
    {
      title: "Reconciliation & Control",
      items: [
        {
          name: "Control Account Flag",
          value: resolveOptionLabel(
            templateProdDetail?.controlAccountFlagOptions,
            reconcilationAndControl.controlAccountFlag,
          ),
        },
        {
          name: "Reconciliation Required",
          value: yesNo(reconcilationAndControl.reconciliationRequired),
        },
        ...(reconcilationAndControl.reconciliationRequired
          ? [
              {
                name: "External Reconciliation Code",
                value: formatValue(
                  reconcilationAndControl.externalReconciliationCode,
                ),
              },
            ]
          : []),
        {
          name: "Settlement Flag",
          value: resolveOptionLabel(
            templateProdDetail?.externalSettlementAccount,
            reconcilationAndControl.settlementFlag,
          ),
        },
      ],
    },
  ];

  const renderSection = (title, items) => (
    <div
      style={{
        boxShadow: "0px 0px 30px 5px rgba(0,0,0,0.025)",
        marginBottom: 25,
      }}
    >
      <Divider orientation="left" orientationMargin="0">
        {title}
      </Divider>
      {items.map((detail, index) => {
        const { name, value } = detail;

        return (
          <div
            style={{
              border: "2px solid #F5F5F5",
              borderRadius: 15,
              borderBottomLeftRadius: 0,
              borderBottomRightRadius: 0,
              borderBottomColor: "#E2E2E2",
            }}
            key={`${title}-${index}`}
          >
            <Row>
              <Col
                span={12}
                style={{
                  backgroundColor: "#F5F5F5",
                  padding: 10,
                  borderRadius: 15,
                  borderTopRightRadius: 0,
                  borderBottomRightRadius: 0,
                  borderBottomLeftRadius: 0,
                }}
              >
                <Text strong>{name}</Text>
              </Col>
              <Col
                span={12}
                style={{
                  padding: 10,
                }}
              >
                <Text className="of-bold-disable-input">{value}</Text>
              </Col>
            </Row>
          </div>
        );
      })}
    </div>
  );

  return (
    <Card
      style={{
        marginBottom: "2%",
        boxShadow: "0px 0px 30px 5px rgba(0,0,0,0.025)",
      }}
    >
      {sections.map((section) => renderSection(section.title, section.items))}
    </Card>
  );
};

export default Preview;
